import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  updateProfile,
} from 'firebase/auth';
import { resolveStakkoFirebase } from '../firebase';
import type { StakkoFirebase, StakkoFirebaseOverrides } from '../firebase';
import { FREE_ENTITLEMENTS, hasStakkoPremium, subscribeStakkoEntitlements } from '../services/entitlements';
import {
  acceptStakkoFriendRequest,
  declineStakkoFriendRequest,
  findStakkoUserByEmail,
  removeStakkoFriend,
  sendStakkoFriendRequest,
  subscribeStakkoFriendships,
  toStakkoFriendView,
} from '../services/friends';
import type { StakkoFriendIdentity } from '../services/friends';
import {
  markAllStakkoNotificationsRead,
  markStakkoNotificationRead,
  subscribeStakkoNotifications,
} from '../services/notifications';
import { ensureStakkoProfile, subscribeStakkoProfile } from '../services/profile';
import {
  earnStakkoCredits,
  ensureStakkoWallet,
  spendStakkoCredits,
  subscribeStakkoWallet,
  subscribeStakkoWalletTransactions,
} from '../services/wallet';
import { STAKKO_EVENTS, stakkoBus } from '../bus';
import type {
  StakkoAppId,
  StakkoAuthUser,
  StakkoEntitlements,
  StakkoFriendship,
  StakkoFriendStatus,
  StakkoFriendView,
  StakkoNotification,
  StakkoProfile,
  StakkoSettingsExtension,
  StakkoTheme,
  StakkoWallet,
  StakkoWalletTransaction,
} from '../types';

const THEME_KEY = 'stakko:theme';

const EMPTY_WALLET: StakkoWallet = {
  balance: 0,
  lifetimeEarned: 0,
  lifetimeSpent: 0,
};

export interface StakkoContextValue {
  appId: StakkoAppId;
  firebase: StakkoFirebase;
  externalAuth: boolean;
  loading: boolean;
  user: StakkoAuthUser | null;
  profile: StakkoProfile | null;
  entitlements: StakkoEntitlements;
  wallet: StakkoWallet;
  transactions: StakkoWalletTransaction[];
  friendships: StakkoFriendship[];
  friends: StakkoFriendView[];
  incomingRequests: StakkoFriendView[];
  notifications: StakkoNotification[];
  unreadNotifications: number;
  theme: StakkoTheme;
  setTheme: (theme: StakkoTheme) => void;
  settingsExtension: StakkoSettingsExtension | null;
  setSettingsExtension: (extension: StakkoSettingsExtension | null) => void;
  hasApp: (appId?: StakkoAppId) => boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  spendCredits: (
    amount: number,
    reason: string,
    meta?: Record<string, unknown>,
  ) => Promise<StakkoWallet>;
  earnCredits: (
    amount: number,
    reason: string,
    meta?: Record<string, unknown>,
  ) => Promise<StakkoWallet>;
  findUserByEmail: (email: string) => Promise<StakkoFriendIdentity | null>;
  sendFriendRequest: (to: StakkoFriendIdentity) => Promise<StakkoFriendStatus>;
  acceptFriendRequest: (id: string) => Promise<void>;
  declineFriendRequest: (id: string) => Promise<void>;
  removeFriend: (id: string) => Promise<void>;
  markNotificationRead: (id: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
}

const StakkoContext = createContext<StakkoContextValue | null>(null);

export interface StakkoProviderProps {
  appId: StakkoAppId;
  children: ReactNode;
  firebase?: StakkoFirebaseOverrides;
  /**
   * Pro aplikace, které mají vlastní (třeba compat) auth. Když je hodnota
   * definovaná, Core nebude poslouchat vlastní Firebase Auth a použije tuhle
   * identitu. `null` znamená „nepřihlášený“.
   */
  authUser?: StakkoAuthUser | null;
  /** Skryje přihlašovací formulář – přihlášení řeší hostitelská aplikace. */
  externalAuth?: boolean;
}

function readStoredTheme(): StakkoTheme {
  try {
    const value = localStorage.getItem(THEME_KEY);
    if (value === 'light' || value === 'dark' || value === 'auto') return value;
  } catch {
    /* private mode */
  }
  return 'light';
}

export function StakkoProvider({
  appId,
  children,
  firebase,
  authUser,
  externalAuth = false,
}: StakkoProviderProps) {
  const resolved = useMemo(() => resolveStakkoFirebase(firebase), [firebase]);
  const { auth, db } = resolved;

  const [user, setUser] = useState<StakkoAuthUser | null>(authUser ?? null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<StakkoProfile | null>(null);
  const [entitlements, setEntitlements] = useState<StakkoEntitlements>(FREE_ENTITLEMENTS);
  const [wallet, setWallet] = useState<StakkoWallet>(EMPTY_WALLET);
  const [transactions, setTransactions] = useState<StakkoWalletTransaction[]>([]);
  const [friendships, setFriendships] = useState<StakkoFriendship[]>([]);
  const [notifications, setNotifications] = useState<StakkoNotification[]>([]);
  const [theme, setThemeState] = useState<StakkoTheme>(readStoredTheme);
  const [settingsExtension, setSettingsExtension] = useState<StakkoSettingsExtension | null>(null);

  useEffect(() => {
    if (authUser !== undefined) {
      setUser(authUser);
      setLoading(false);
      stakkoBus.emit(
        STAKKO_EVENTS.authChanged,
        authUser ? { uid: authUser.uid } : null,
        false,
      );
      return;
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      (nextUser) => {
        setUser(nextUser);
        setLoading(false);
        stakkoBus.emit(STAKKO_EVENTS.authChanged, nextUser ? { uid: nextUser.uid } : null, false);
      },
      (error) => {
        console.warn('[stakko] auth listener selhal', error);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [auth, authUser]);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setEntitlements(FREE_ENTITLEMENTS);
      setWallet(EMPTY_WALLET);
      setTransactions([]);
      setFriendships([]);
      setNotifications([]);
      return;
    }

    const uid = user.uid;
    let alive = true;

    void ensureStakkoProfile(db, user, appId).catch((error) => {
      console.warn('[stakko] profil se nepodařilo zapsat (běží Firestore pravidla?)', error);
    });
    void ensureStakkoWallet(db, uid).catch((error) => {
      console.warn('[stakko] peněženku se nepodařilo vytvořit', error);
    });

    const unsubscribeProfile = subscribeStakkoProfile(db, uid, (next) => {
      if (alive) setProfile(next);
    });
    const unsubscribeEntitlements = subscribeStakkoEntitlements(db, uid, (next) => {
      if (!alive) return;
      setEntitlements(next);
      stakkoBus.emit(STAKKO_EVENTS.entitlementsChanged, next, false);
    });
    const unsubscribeWallet = subscribeStakkoWallet(db, uid, (next) => {
      if (!alive) return;
      setWallet(next);
      stakkoBus.emit(STAKKO_EVENTS.walletChanged, next, false);
    });
    const unsubscribeTransactions = subscribeStakkoWalletTransactions(db, uid, (next) => {
      if (alive) setTransactions(next);
    });
    const unsubscribeFriends = subscribeStakkoFriendships(db, uid, (next) => {
      if (alive) setFriendships(next);
    });
    const unsubscribeNotifications = subscribeStakkoNotifications(db, uid, (next) => {
      if (!alive) return;
      setNotifications(next);
      stakkoBus.emit(STAKKO_EVENTS.notificationsChanged, next, false);
    });

    return () => {
      alive = false;
      unsubscribeProfile();
      unsubscribeEntitlements();
      unsubscribeWallet();
      unsubscribeTransactions();
      unsubscribeFriends();
      unsubscribeNotifications();
    };
  }, [user, appId, db]);

  const setTheme = useCallback((next: StakkoTheme) => {
    setThemeState(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* private mode */
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      const code = (error as { code?: string }).code;
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/operation-not-supported-in-this-environment' ||
        code === 'auth/cancelled-popup-request'
      ) {
        await signInWithRedirect(auth, provider);
        return;
      }
      throw error;
    }
  }, [auth]);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    },
    [auth],
  );

  const signUpWithEmail = useCallback(
    async (email: string, password: string, displayName?: string) => {
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const name = displayName?.trim();
      if (name) {
        await updateProfile(credential.user, { displayName: name });
        const current = auth.currentUser;
        if (current) {
          await ensureStakkoProfile(db, current, appId).catch(() => undefined);
        }
      }
    },
    [auth, db, appId],
  );

  const sendPasswordReset = useCallback(
    async (email: string) => {
      await sendPasswordResetEmail(auth, email.trim());
    },
    [auth],
  );

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, [auth]);

  const spendCredits = useCallback(
    async (amount: number, reason: string, meta?: Record<string, unknown>) => {
      if (!user) throw new Error('NOT_AUTHENTICATED');
      const next = await spendStakkoCredits(db, user.uid, amount, reason, appId, meta);
      stakkoBus.emit(STAKKO_EVENTS.creditsSpent, { amount, reason });
      return next;
    },
    [db, user, appId],
  );

  const earnCredits = useCallback(
    async (amount: number, reason: string, meta?: Record<string, unknown>) => {
      if (!user) throw new Error('NOT_AUTHENTICATED');
      const next = await earnStakkoCredits(db, user.uid, amount, reason, appId, meta);
      stakkoBus.emit(STAKKO_EVENTS.creditsEarned, { amount, reason });
      return next;
    },
    [db, user, appId],
  );

  const findUserByEmail = useCallback(
    async (email: string) => findStakkoUserByEmail(db, email),
    [db],
  );

  const sendFriendRequest = useCallback(
    async (to: StakkoFriendIdentity) => {
      if (!user) throw new Error('NOT_AUTHENTICATED');
      const from: StakkoFriendIdentity = {
        uid: user.uid,
        email: user.email,
        displayName: profile?.displayName || user.displayName || user.email?.split('@')[0] || 'Student',
        photoURL: profile?.photoURL || user.photoURL,
      };
      return sendStakkoFriendRequest(db, from, to);
    },
    [db, user, profile],
  );

  const acceptFriendRequest = useCallback(
    async (id: string) => {
      if (!user) throw new Error('NOT_AUTHENTICATED');
      await acceptStakkoFriendRequest(db, id, user.uid, {
        uid: user.uid,
        email: user.email,
        displayName: profile?.displayName || user.displayName,
        photoURL: profile?.photoURL || user.photoURL,
      });
    },
    [db, user, profile],
  );

  const declineFriendRequest = useCallback(
    async (id: string) => {
      if (!user) throw new Error('NOT_AUTHENTICATED');
      await declineStakkoFriendRequest(db, id, user.uid);
    },
    [db, user],
  );

  const removeFriend = useCallback(
    async (id: string) => {
      if (!user) throw new Error('NOT_AUTHENTICATED');
      await removeStakkoFriend(db, id);
    },
    [db, user],
  );

  const markNotificationRead = useCallback(
    async (id: string) => {
      if (!user) return;
      await markStakkoNotificationRead(db, user.uid, id);
    },
    [db, user],
  );

  const markAllNotificationsRead = useCallback(async () => {
    if (!user) return;
    await markAllStakkoNotificationsRead(db, user.uid, notifications);
  }, [db, user, notifications]);

  const hasApp = useCallback(
    (target?: StakkoAppId) => hasStakkoPremium(entitlements, target ?? appId),
    [entitlements, appId],
  );

  const friends = useMemo(
    () =>
      friendships
        .filter((item) => item.status === 'accepted')
        .map((item) => toStakkoFriendView(item, user?.uid ?? '')),
    [friendships, user?.uid],
  );

  const incomingRequests = useMemo(
    () =>
      friendships
        .map((item) => toStakkoFriendView(item, user?.uid ?? ''))
        .filter((item) => item.incoming),
    [friendships, user?.uid],
  );

  const unreadNotifications = useMemo(
    () => notifications.filter((item) => !item.readAt).length,
    [notifications],
  );

  const value = useMemo<StakkoContextValue>(
    () => ({
      appId,
      firebase: resolved,
      externalAuth,
      loading,
      user,
      profile,
      entitlements,
      wallet,
      transactions,
      friendships,
      friends,
      incomingRequests,
      notifications,
      unreadNotifications,
      theme,
      setTheme,
      settingsExtension,
      setSettingsExtension,
      hasApp,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      sendPasswordReset,
      signOut,
      spendCredits,
      earnCredits,
      findUserByEmail,
      sendFriendRequest,
      acceptFriendRequest,
      declineFriendRequest,
      removeFriend,
      markNotificationRead,
      markAllNotificationsRead,
    }),
    [
      appId,
      resolved,
      externalAuth,
      loading,
      user,
      profile,
      entitlements,
      wallet,
      transactions,
      friendships,
      friends,
      incomingRequests,
      notifications,
      unreadNotifications,
      theme,
      setTheme,
      settingsExtension,
      setSettingsExtension,
      hasApp,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      sendPasswordReset,
      signOut,
      spendCredits,
      earnCredits,
      findUserByEmail,
      sendFriendRequest,
      acceptFriendRequest,
      declineFriendRequest,
      removeFriend,
      markNotificationRead,
      markAllNotificationsRead,
    ],
  );

  return <StakkoContext.Provider value={value}>{children}</StakkoContext.Provider>;
}

export function useStakko(): StakkoContextValue {
  const value = useContext(StakkoContext);
  if (!value) {
    throw new Error('useStakko musí být uvnitř <StakkoProvider>.');
  }
  return value;
}

export function useStakkoOptional(): StakkoContextValue | null {
  return useContext(StakkoContext);
}
